import moment from 'moment';
/**
 * Calculate UKG/DEPOSIT export rows matching exportCdpCompleteReport logic.
 * Returns an array of rows ready for UKG export (PROVIDER, UKG ID, UKG CODE, PAY TYPE, SPECIALITY, PAY MONTH, 1ST DEPOSIT, 2ND DEPOSIT, AMOUNT)
 *
 * @param {Array} data
 * @param {Function} getDoctorFullName
 * @param {String|Date} startDate
 */
export function calculateCdpDepositFields(data, getDoctorFullName, startDate) {
  // Use the canonical deposit entries (which are already merged per-location) and create one
  // UKG row per canonical deposit grouping so UKG sheets preserve multiple COMM rows per provider.
  const firstDepositEntries = getDepositData(data, 'first', startDate, false) || [];
  const secondDepositEntries = getDepositData(data, 'second', startDate, false) || [];

  // Build a quick lookup for provider metadata (payType/role/payMonth) keyed by ukgid||ABBR
  const providerLookupByAbbr = new Map((data || []).map(item => [`${item.ABBR}`, item]));

  // Use the same grouping fields getDepositData uses when creating merged entries so we don't collapse
  // distinct deposit rows that differ by practice/sage location, etc.
  const makeGroupKey = (it) => {
    if (!it) return '';
    // Special-case REIMB: aggregate per provider (not per practice)
    // REIMB should combine reimbursements across locations; other codes remain per-practice
    const payType = providerLookupByAbbr.get(it.ABBR)?.payType;
    const payTypeCode = payType === '1099' ? payType : undefined;
    const code = (payTypeCode || it.ukgCode || '').toString().toUpperCase();
    it.ukgCode = code;
    if (code === 'REIMB') {
      return `${it.ukgid || ''}||${code}||${it.ABBR || ''}`;
    }
    // Include sageAccount in grouping so notes posted to different GL accounts don't collapse
    return `${it.ukgid || ''}||${it.doctorName || ''}||${it.ukgAccountName || ''}||${code || ''}||${it.ABBR || ''}||${it.sageEntityName || ''}||${it.sageId || ''}||${it.sageAccount || ''}`;
  };

  // Accumulate entries per group key so duplicate keys sum amounts (defensive: handles upstream duplicates)
  const accumulateEntries = (entries) => {
    const m = new Map();
    (entries || []).forEach(e => {
      const k = makeGroupKey(e);
      const amt = Number(e.amount || 0);
      if (!m.has(k)) {
        m.set(k, { ...e, amount: amt });
      } else {
        const ex = m.get(k);
        ex.amount = Number(ex.amount || 0) + amt;
        m.set(k, ex);
      }
    });
    return m;
  };

  const firstMapEntry = accumulateEntries(firstDepositEntries);
  const secondMapEntry = accumulateEntries(secondDepositEntries);

  const combinedKeys = new Set([...firstMapEntry.keys(), ...secondMapEntry.keys()]);

  // Build a quick lookup for provider metadata (payType/role/payMonth) keyed by ukgid||ABBR
  const providerLookup = new Map((data || []).map(item => [`${item.ukgid || ''}||${item.ABBR || ''}`, item]));
  const aggregateMap = new Map();

  combinedKeys.forEach((key) => {
    const firstEntry = firstMapEntry.get(key);
    const secondEntry = secondMapEntry.get(key);
    const sourceEntry = firstEntry || secondEntry;
    if (!sourceEntry) return;

    const firstAmt = firstEntry ? Number(firstEntry.amount || 0) : 0;
    const secondAmt = secondEntry ? Number(secondEntry.amount || 0) : 0;
    const totalAmt = firstAmt + secondAmt;
    if (totalAmt === 0) return;

    // recover provider metadata when available
    const providerKey = `${sourceEntry.ukgid || ''}||${sourceEntry.ABBR || ''}`;
    const providerItem = providerLookup.get(providerKey);

    const providerName = typeof getDoctorFullName === 'function'
      ? (providerItem ? getDoctorFullName(providerItem.ABBR) : (sourceEntry.doctorName || sourceEntry.ABBR))
      : (providerItem?.doctorName || sourceEntry.doctorName || sourceEntry.ABBR);

    const ukgCodeNorm = ((sourceEntry.ukgCode || 'COMM') + '').toString().toUpperCase();
    const mapKey = `${providerName || ''}||${sourceEntry.ukgid || ''}||${ukgCodeNorm}`;

    if (!aggregateMap.has(mapKey)) {
      aggregateMap.set(mapKey, {
        PROVIDER: providerName,
        'ADP ID': sourceEntry.adpId,
        'UKG ID': sourceEntry.ukgid,
        'CO CODE': providerItem?.companyCode || sourceEntry.companyCode || '',
        'UKG CODE': ukgCodeNorm,
        'PAY TYPE': providerItem?.payType || '',
        SPECIALITY: providerItem?.role || '',
        'PAY MONTH': (providerItem && providerItem.payMonth) || (startDate ? moment(startDate).format('MMMM YYYY') : ''),
        '1ST DEPOSIT': firstAmt,
        '2ND DEPOSIT': secondAmt,
        AMOUNT: totalAmt
      });
    } else {
      const ex = aggregateMap.get(mapKey);
      ex['1ST DEPOSIT'] = Number(ex['1ST DEPOSIT'] || 0) + firstAmt;
      ex['2ND DEPOSIT'] = Number(ex['2ND DEPOSIT'] || 0) + secondAmt;
      ex.AMOUNT = Number(ex.AMOUNT || 0) + totalAmt;
      aggregateMap.set(mapKey, ex);
    }
  });

  const finalRows = Array.from(aggregateMap.values()).map(r => ({
    ...r,
    '1ST DEPOSIT': Number(r['1ST DEPOSIT'] || 0).toFixed(2),
    '2ND DEPOSIT': Number(r['2ND DEPOSIT'] || 0).toFixed(2),
    AMOUNT: Number(r.AMOUNT || 0).toFixed(2)
  }));

  // Sort by PROVIDER then UKG ID for stable output
  finalRows.sort((a, b) => {
    const aProv = String(a.PROVIDER || '').toLowerCase();
    const bProv = String(b.PROVIDER || '').toLowerCase();
    if (aProv !== bProv) return aProv.localeCompare(bProv);
    const aId = String(a['UKG ID'] || '').toLowerCase();
    const bId = String(b['UKG ID'] || '').toLowerCase();
    return aId.localeCompare(bId);
  });

  return finalRows;
}

const isNoteFrequency = (note, frequency) => [frequency, 'split'].includes((note.frequency || '').toString().toLowerCase())

export function getDepositData(data, fundName, startDate, isAltPay = false) {
  const filteredData = data || [];
  let practiceFundsObj = {};

  filteredData.forEach((item) => {
    const row = { ...item, ukgCode: item.payType === '1099' ? '1099' : item.ukgCode };
    const practiceSources = Object.keys(item.data || {});

    practiceSources.forEach((practiceKey, index) => {
      const practiceData = item.data?.[practiceKey] || {};
      if (!practiceData) return;

      const filteredNotes = practiceData.financialNoteList?.filter(fn => isNoteFrequency(fn, fundName)) ?? [];
      // Compute the total of all notes that will be redistributed across BOTH deposits so the
      // deduction matches exactly what gets added back — prevents double-counting when the
      // precomputed practiceData.financialNotes diverges from the actual list totals.
      const totalValidNotes = (practiceData.financialNoteList || []).reduce((sum, fn) => {
        const freq = (fn.frequency || '').toLowerCase();
        return (['first', 'second', 'split'].includes(freq))
          ? sum + (parseFloat(fn.financialNotes) || 0)
          : sum;
      }, 0);
      const practicePayTotal = practiceData.practiceTotalPay ?? practiceData.procBasedPay;
      const basePay = isAltPay ? practicePayTotal : (practicePayTotal - totalValidNotes) / 2;
      const locationId = (item.sage_ID && item.sage_ID[index]) || practiceData.sageId || '';
      if (!practiceFundsObj[locationId]) {
        practiceFundsObj[locationId] = [];
      }

      const ukgCodeMap = {};
      const baseSageAccount = practiceData.sageAccount || item.sageAccount || '2230';

      filteredNotes.forEach((finNote) => {
        let ukgCode = (item.payType === '1099' ? '1099' : finNote.ukg || 'COMM').toString().toUpperCase();
        const noteSageAccount = finNote.sageAccount || baseSageAccount;
        const mapKey = `${ukgCode}||${noteSageAccount}`;
        if (!ukgCodeMap[mapKey]) ukgCodeMap[mapKey] = { ukgCode, amount: 0, sageAccount: noteSageAccount };

        let fNCost = 0;
        const freq = (finNote.frequency || '').toLowerCase();
        if (freq === fundName) {
          fNCost = Number(finNote.financialNotes);
        } else if (freq === 'split') {
          fNCost = Number(finNote.financialNotes) / 2;
        }
        ukgCodeMap[mapKey].amount += fNCost;
      });

      const commKey = `${item.payType === '1099' ? '1099' : 'COMM'}||${baseSageAccount}`;
      if (!ukgCodeMap[commKey]) ukgCodeMap[commKey] = { ukgCode: row.ukgCode, amount: 0, sageAccount: baseSageAccount };
      ukgCodeMap[commKey].amount += basePay;

      Object.values(ukgCodeMap).forEach(codeData => {
        if (codeData.amount !== 0) {
          practiceFundsObj[locationId].push({
            ukgid: item.ukgid || '',
            adpId: item.adpId || '',            companyCode: item.companyCode || '',            doctorName: item.doctorName,
            sageEntityName: practiceData.sageEntityName || item.sageEntityName || '',
            ukgAccountName: item.ukgAccountName || '',
            ukgCode: codeData.ukgCode,
            ABBR: item.ABBR || '',
            amount: codeData.amount,
            sageId: practiceData.sageId || (item.sage_ID && item.sage_ID[index]) || '',
            sageAccount: codeData.sageAccount || baseSageAccount
          });
        }
      });
    });
  });

  const sortedArray = Object.keys(practiceFundsObj).sort().reduce((result, key) => {
    result[key] = practiceFundsObj[key];
    return result;
  }, {});

  const providerLocationArr = Object.values(sortedArray).flat();

  providerLocationArr.sort((a, b) => {
    const aId = String(a.ukgid || '').toLowerCase();
    const bId = String(b.ukgid || '').toLowerCase();
    return aId.localeCompare(bId);
  });

  const makeGroupKey = (it) => {
    if (!it) return '';
    const code = (it.ukgCode || '').toString().toUpperCase();
    // Include sageId for all codes (including REIMB) so each practice gets its own row.
    // Previously REIMB was aggregated per-provider, but that caused all REI rows in the ADP
    // sheet to share the same Temp Cost Number regardless of which practice originated them.
    return `${it.ukgid || ''}||${it.doctorName || ''}||${it.ukgAccountName || ''}||${code || ''}||${it.ABBR || ''}||${it.sageEntityName || ''}||${it.sageId || ''}||${it.sageAccount || ''}`;
  };

  const mergedMap = new Map();
  providerLocationArr.forEach(item => {
    const key = makeGroupKey(item);
    if (!mergedMap.has(key)) {
      mergedMap.set(key, { ...item, amount: Number(item.amount || 0) });
    } else {
      const existing = mergedMap.get(key);
      existing.amount += Number(item.amount || 0);
      mergedMap.set(key, existing);
    }
  });

  const mergedArray = Array.from(mergedMap.values()).map(it => ({
    ...it,
    amount: Math.round((Number(it.amount || 0) + Number.EPSILON) * 100) / 100
  }));

  return mergedArray;
}
